import React, { createContext, useState, useContext, useEffect } from 'react';
import { loadSessionProfile } from '../services/sessionProfile';

const AuthContext = createContext(null);

// Persist active role in localStorage so it survives page reloads
const ACTIVE_ROLE_KEY = 'agent_active_role';

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem('agent_token'));
    const [agentInfo, setAgentInfo] = useState(null);
    const [agentRoles, setAgentRoles] = useState([]);
    const [activeRole, setActiveRoleState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionError, setSessionError] = useState('');
    const [sessionAttempt, setSessionAttempt] = useState(0);

    const logout = React.useCallback(() => {
        localStorage.removeItem('agent_token');
        localStorage.removeItem(ACTIVE_ROLE_KEY);
        setToken(null);
        setAgentInfo(null);
        setAgentRoles([]);
        setActiveRoleState(null);
        setSessionError('');
    }, []);

    const retrySession = React.useCallback(() => {
        setLoading(true);
        setSessionError('');
        setSessionAttempt(value => value + 1);
    }, []);

    useEffect(() => {
        let current = true;
        const controller = new AbortController();
        setSessionError('');
        setAgentInfo(null);
        setAgentRoles([]);
        setActiveRoleState(null);
        if (token) {
            setLoading(true);
            // Verify token/get agent profile (includes roles now)
            loadSessionProfile(token, { signal: controller.signal })
                .then(data => {
                    if (!current) return;
                    setAgentInfo(data);
                    const roles = data.roles || [];
                    setAgentRoles(roles);

                    // Restore persisted active role if still valid
                    try {
                        const stored = JSON.parse(localStorage.getItem(ACTIVE_ROLE_KEY));
                        const stillValid = stored && roles.some(r => r.id === stored.id);
                        setActiveRoleState(stillValid ? stored : (roles[0] || null));
                    } catch {
                        setActiveRoleState(roles[0] || null);
                    }

                    setLoading(false);
                })
                .catch(err => {
                    if (!current) return;
                    // Network errors (offline, backend restart, timeouts) should not
                    // wipe out a still-valid session; only a confirmed 401/403 does.
                    if (err.isAuthError) {
                        logout();
                    } else {
                        setSessionError(err.message || 'No hemos podido conectar. Reintenta la conexión.');
                    }
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
        return () => { current = false; controller.abort(); };
    }, [token, sessionAttempt, logout]);

    const setActiveRole = (role) => {
        setActiveRoleState(role);
        if (role) {
            localStorage.setItem(ACTIVE_ROLE_KEY, JSON.stringify(role));
        } else {
            localStorage.removeItem(ACTIVE_ROLE_KEY);
        }
    };

    const login = React.useCallback((newToken) => {
        setLoading(true);
        setSessionError('');
        localStorage.setItem('agent_token', newToken);
        setToken(newToken);
    }, []);

    const authFetch = React.useCallback(async (url, options = {}) => {
        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${token}`
        };

        const response = await fetch(url, { ...options, headers });

        // Only logout if 401 comes from our own auth endpoints, not external APIs
        // or infra errors (proxies/CDNs returning a bare 401/HTML on outage).
        if (response.status === 401) {
            try {
                const data = await response.clone().json();
                // Don't logout if it's an external API error
                if (!data.external_error) {
                    logout();
                    throw new Error('Session expired');
                }
            } catch (e) {
                if (e.message === 'Session expired') throw e;
                // Body wasn't JSON (e.g. gateway/error page): can't confirm this is
                // really our auth middleware rejecting the token, so keep the session.
            }
        }

        return response;
    }, [token, logout]);

    return (
        <AuthContext.Provider value={{ token, agentInfo, agentRoles, activeRole, setActiveRole, login, logout, authFetch, loading, sessionError, retrySession }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
